import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { isSettingsWindowDocument } from '@suite/settings';
import { SuiteTopbar } from '@/components/SuiteTopbar';
import { AppDialogHost } from '@/components/AppDialogHost';
import { SettingsDialog } from '@/settings/SettingsDialog';
import { HomePage } from '@/pages/HomePage';
import { LoginPage } from '@/pages/auth/LoginPage';
import { RegisterPage } from '@/pages/auth/RegisterPage';
import { DashboardLayout } from '@/pages/dashboard/DashboardLayout';
import { DrivePage } from '@/pages/dashboard/DrivePage';
import { AdminLayout } from '@/pages/admin/AdminLayout';
import { AdminDashboard } from '@/pages/admin/AdminDashboard';
import { UsersPage } from '@/pages/admin/UsersPage';
import { ManageUsersPage } from '@/pages/admin/ManageUsersPage';
import { ActivitiesPage } from '@/pages/admin/ActivitiesPage';
import { RequestLandingPage } from '@/pages/RequestLandingPage';
import { ShareLandingPage } from '@/pages/ShareLandingPage';
import { useShortcutDispatcher } from '@/hooks/useShortcutDispatcher';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }
  
  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent"></div>
      </div>
    );
  }
  
  if (!isAuthenticated || user?.role !== 'ADMIN') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <>{children}</>;
}

// The shared two-column settings surface, opened in its own window
// (?view=settings — settings-window plan).
const SettingsWindowPage = lazy(() =>
  import('@/settings/SettingsWindowPage').then((m) => ({ default: m.SettingsWindowPage })),
);

export function App() {
  // Fires the help center's user-assigned shortcuts app-wide.
  useShortcutDispatcher();

  // The settings window boots this same SPA with ?view=settings: a bare
  // two-column settings surface — no app chrome (settings-window plan). It
  // needs no session, so it renders before the routed, auth-gated chrome.
  if (isSettingsWindowDocument()) {
    return (
      <Suspense fallback={null}>
        <SettingsWindowPage />
        {/* Every app document shows its own dialogs — never the browser's. */}
        <AppDialogHost />
      </Suspense>
    );
  }

  return (
    // Suite chrome first, app underneath: the shared top bar sits above every
    // page, the routed content fills the remaining height.
    <div className="flex min-h-screen flex-col bg-background">
      <SuiteTopbar />
      <div className="flex min-h-0 flex-1 flex-col">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/auth/login" element={<LoginPage />} />
          <Route path="/auth/register" element={<RegisterPage />} />
          {/* Public file-request door: receivers need no account. */}
          <Route path="/request/:shortId" element={<RequestLandingPage />} />
          {/* Public share door: unlock + download, no account needed. */}
          <Route path="/s/:shortId" element={<ShareLandingPage />} />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DrivePage />} />
          </Route>

          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminLayout />
              </AdminRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="manage" element={<ManageUsersPage />} />
            <Route path="activities" element={<ActivitiesPage />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      {/* In-app settings dialog — the default settings surface (settings-window
          plan); the separate window renders the same sections via
          SettingsWindowPage instead. */}
      <SettingsDialog />
      {/* Global app dialog host — the single replacement for native browser
          alert/confirm/prompt (shared-components plan). */}
      <AppDialogHost />
    </div>
  );
}
